import React, { useMemo } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { Helmet } from 'react-helmet-async';
import 'react-toastify/dist/ReactToastify.css';
import { AvatarProvider } from './context/AvatarContext';
import { DocumentProvider } from './context/DocumentContext';

// Page imports
import Admin from './pages/Admin';
import SharedSetView from './pages/SharedSetView';
import Inventory from './pages/Inventory';
import AddCoin from './pages/AddCoin';
import ViewCoin from './pages/ViewCoin';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Profile from './pages/Profile';
import Transactions from './pages/Transactions';
import Contacts from './pages/Contacts';
import Submissions from './pages/Submissions';
import ViewSubmission from './pages/ViewSubmission';
import Sets from './pages/Sets';
import SetDetailsPage from './pages/SetDetailsPage';
import Dashboard from './pages/Dashboard';
import ContactDetails from './pages/ContactDetails';
import Documents from './pages/Documents';
import DocumentView from './pages/DocumentView';
import PopulationHistory from './components/PopulationHistory';
import Auctions from './pages/Auctions';
import AuctionDetailsPage from './features/auctions/AuctionDetailsPage';
import AuctionLotDetailsPage from './features/auctions/AuctionLotDetailsPage';

// Component imports
import TransactionEditForm from './components/TransactionEditForm';
import ContactForm from './components/ContactForm';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import CSRFDiagnostics from './components/CSRFDiagnostics';

function App() {
  const favicon = useMemo(() => {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? '/favicon-dev.svg'
      : '/favicon.svg';
  }, []);

  return (
    <AvatarProvider>
      <Helmet>
        <link rel="icon" type="image/svg+xml" href={favicon} />
      </Helmet>
      <DocumentProvider>
        <ToastContainer position="top-right" />
      <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/shared/:shareId" element={<SharedSetView />} />
          <Route element={<Layout />}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAdmin>
                  <Admin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/csrf-diagnostics"
              element={
                <ProtectedRoute>
                  <CSRFDiagnostics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory"
              element={
                <ProtectedRoute>
                  <Inventory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/coins/add"
              element={
                <ProtectedRoute>
                  <AddCoin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/coins/:id"
              element={
                <ProtectedRoute>
                  <ViewCoin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/transactions"
              element={
                <ProtectedRoute>
                  <Transactions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/transactions/:id/edit"
              element={
                <ProtectedRoute>
                  <TransactionEditForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contacts"
              element={
                <ProtectedRoute>
                  <Contacts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contacts/new"
              element={
                <ProtectedRoute>
                  <ContactForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contacts/:id"
              element={
                <ProtectedRoute>
                  <ContactDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contacts/:id/edit"
              element={
                <ProtectedRoute>
                  <ContactForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/submissions"
              element={
                <ProtectedRoute>
                  <Submissions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/submissions/:id"
              element={
                <ProtectedRoute>
                  <ViewSubmission />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sets"
              element={
                <ProtectedRoute>
                  <Sets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sets/:setId"
              element={
                <ProtectedRoute>
                  <SetDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/documents"
              element={
                <ProtectedRoute>
                  <Documents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/documents/:id"
              element={
                <ProtectedRoute>
                  <DocumentView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/population-history/:notificationId"
              element={
                <ProtectedRoute>
                  <PopulationHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/auctions"
              element={
                <ProtectedRoute>
                  <Auctions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/auctions/system/:id"
              element={
                <ProtectedRoute>
                  <AuctionDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/auctions/:id"
              element={
                <ProtectedRoute>
                  <AuctionDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/auctions/:auctionId/lots/:lotNumber"
              element={
                <ProtectedRoute>
                  <AuctionLotDetailsPage />
                </ProtectedRoute>
              }
            />
          </Route>
      </Routes>
      </DocumentProvider>
    </AvatarProvider>
  );
}

export default App;
